import { Env } from '@/libs/Env'
import { API_ROUTES, API_ERROR_MESSAGES } from '@super/shared-types'

/**
 * API Client Error for better error handling
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public response?: Response,
    public body?: unknown,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

/**
 * Base API Client configuration
 */
interface BaseClientConfig {
  baseUrl?: string
  defaultHeaders?: Record<string, string>
}

/**
 * Base API Client class
 * All other API clients extend from this
 */
export class BaseApiClient {
  protected baseUrl: string
  protected defaultHeaders: Record<string, string>

  constructor(config: BaseClientConfig = {}) {
    this.baseUrl = config.baseUrl || (Env.VITE_API_URL as string)
    this.defaultHeaders = {
      'Content-Type': 'application/json',
      ...config.defaultHeaders,
    }
  }

  /**
   * Generic fetch wrapper with error handling
   */
  protected async request<T>(
    path: string,
    options: RequestInit = {},
  ): Promise<T> {
    const url = `${this.baseUrl}${path}`

    const response = await fetch(url, {
      ...options,
      credentials: 'include',
      headers: {
        ...this.defaultHeaders,
        ...options.headers,
      },
    })

    if (!response.ok) {
      // Handle session expiration - redirect to login
      if (response.status === 401) {
        try {
          const errorBody = await response.json()
          if (errorBody.message === API_ERROR_MESSAGES.INVALID_SESSION_COOKIE) {
            // Session has expired, redirect to login
            window.location.href = `${this.baseUrl}${API_ROUTES.AUTH.LOGIN}`
            // Throw to prevent further processing
            throw new ApiError(
              'Session expired. Redirecting to login...',
              401,
              response,
            )
          }
        } catch (parseError) {
          // If we can't parse the response body, fall through to generic error handling
          if (parseError instanceof ApiError) {
            throw parseError
          }
        }
      }

      let errorMessage = `API request failed: ${response.status} ${response.statusText}`
      let errorBody: unknown
      try {
        errorBody = await response.clone().json()
        const parsed = errorBody as { message?: string; error?: string }
        if (parsed?.message) {
          errorMessage = parsed.message
        } else if (parsed?.error) {
          errorMessage = parsed.error
        }
      } catch {
        // If we can't parse the error body, use the default message
      }
      throw new ApiError(errorMessage, response.status, response, errorBody)
    }

    return response.json()
  }

  /**
   * Helper method to build query strings
   */
  protected buildQueryString(params: Record<string, any>): string {
    const queryParams = new URLSearchParams()

    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        queryParams.append(key, value.toString())
      }
    })

    const queryString = queryParams.toString()
    return queryString ? `?${queryString}` : ''
  }
}
